import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, Search, FileText, Upload, Eye, Trash2, RefreshCw,
  Building2, Users, Package, Truck, Clock, CheckCircle, XCircle,
  AlertTriangle, Filter, Download, Sparkles, Loader2, Edit2
} from 'lucide-react';
import {
  getCompanies, getClients, createClient,
  createClientPO, getClientPOs, getClientPO, updateClientPO, cancelClientPO,
  uploadClientPODocument, getClientPODocument, getClientPOStats, canCreateDelivery,
  extractClientPOData, createClientPOFromExtraction, getMyScopedClientPOs, getMyInfo
} from '../lib/api';
import { useAuth } from '../context/AuthContext';

const statusColors = {
  draft: 'bg-gray-100 text-gray-800',
  active: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800'
};

const poTypeIcons = {
  equipment: Package,
  manpower: Users,
  material: FileText,
  transportation: Truck
};

export const ClientPOPage = () => {
  const { user } = useAuth();
  const isSalesOnly = user?.role === 'sales';
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  
  // Dialog states
  const [createPOOpen, setCreatePOOpen] = useState(false);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [viewPOOpen, setViewPOOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  
  // OCR States
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [ocrExtracting, setOcrExtracting] = useState(false);
  const [ocrExtraction, setOcrExtraction] = useState(null);
  const [ocrReviewOpen, setOcrReviewOpen] = useState(false);
  const [ocrCreating, setOcrCreating] = useState(false);
  const ocrFileInputRef = useRef(null);
  
  // Form states
  const [poForm, setPOForm] = useState({
    company_id: '',
    client_id: '',
    po_number: '',
    po_type: 'equipment',
    po_value_type: 'specified', // NEW: specified or open
    po_date: new Date().toISOString().split('T')[0],
    valid_from: '',
    valid_to: '',
    currency: 'SAR',
    total_value: 0,
    vat_rate: 15,
    // NEW: Rental details for equipment PO
    rental_basis: 'monthly', // daily, weekly, monthly
    rental_rate: 0,
    equipment_category: '',
    notes: '',
    items: []
  });
  
  const [clientForm, setClientForm] = useState({
    company_id: '',
    name: '',
    trade_name: '',
    registration_number: '',
    vat_number: '',
    address: '',
    city: '',
    country: 'Saudi Arabia',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    payment_terms: 30,
    credit_limit: 0
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [companiesRes, clientsRes] = await Promise.all([
        getCompanies(),
        getClients()
      ]);
      // For sales users, restrict company list to their own assigned companies only
      let filteredCompanies = companiesRes.data;
      if (isSalesOnly && user?.company_ids?.length) {
        filteredCompanies = companiesRes.data.filter(c => user.company_ids.includes(c.id));
      }
      setCompanies(filteredCompanies);
      setClients(clientsRes.data);

      // Fetch POs — sales users only see their own via scoped endpoint
      let posRes;
      if (isSalesOnly) {
        posRes = await getMyScopedClientPOs(selectedCompany || null, statusFilter || null);
      } else {
        const filters = {};
        if (selectedCompany) filters.company_id = selectedCompany;
        if (statusFilter) filters.status = statusFilter;
        if (typeFilter) filters.po_type = typeFilter;
        posRes = await getClientPOs(filters);
      }
      const statsRes = await getClientPOStats(selectedCompany).catch(() => ({ data: null }));
      setPurchaseOrders(posRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, statusFilter, typeFilter, isSalesOnly, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePO = async () => {
    try {
      if (!poForm.company_id || !poForm.client_id || !poForm.po_number || !poForm.po_type) {
        toast.error('Please fill all required fields');
        return;
      }
      
      await createClientPO(poForm);
      toast.success('Client PO created successfully');
      setCreatePOOpen(false);
      setPOForm({
        company_id: '',
        client_id: '',
        po_number: '',
        po_type: 'equipment',
        po_date: new Date().toISOString().split('T')[0],
        valid_from: '',
        valid_to: '',
        currency: 'SAR',
        total_value: 0,
        vat_rate: 15,
        notes: '',
        items: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    }
  };

  const handleCreateClient = async () => {
    try {
      if (!clientForm.company_id || !clientForm.name) {
        toast.error('Company and name are required');
        return;
      }
      
      await createClient(clientForm);
      toast.success('Client created successfully');
      setCreateClientOpen(false);
      setClientForm({
        company_id: '',
        name: '',
        trade_name: '',
        registration_number: '',
        vat_number: '',
        address: '',
        city: '',
        country: 'Saudi Arabia',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        payment_terms: 30,
        credit_limit: 0
      });
      
      // Refresh clients list
      const clientsRes = await getClients();
      setClients(clientsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };

  const handleUploadDocument = async (file) => {
    if (!selectedPO) return;
    
    try {
      await uploadClientPODocument(selectedPO.id, file);
      toast.success('Document uploaded successfully');
      setUploadOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const handleViewDocument = async (po) => {
    try {
      const res = await getClientPODocument(po.id);
      const { data, content_type, filename } = res.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: content_type });
      
      // Open in new tab
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view document');
    }
  };

  const handleCancelPO = async (poId) => {
    if (!window.confirm('Are you sure you want to cancel this PO?')) return;
    
    try {
      await cancelClientPO(poId);
      toast.success('PO cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel PO');
    }
  };

  // ==================== OCR EXTRACTION HANDLERS ====================
  
  const handleOcrFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, WebP image or PDF document');
      return;
    }
    
    // Max 20MB
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 20MB');
      return;
    }
    
    setOcrExtracting(true);
    try {
      const response = await extractClientPOData(file);
      setOcrExtraction(response.data);
      setOcrDialogOpen(false);
      setOcrReviewOpen(true);
      toast.success('PO data extracted! Please review.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract PO data');
    } finally {
      setOcrExtracting(false);
      e.target.value = '';
    }
  };
  
  const handleCreatePOFromOCR = async () => {
    if (!ocrExtraction?.extraction_id) return;
    
    // Need to select a company first
    if (!selectedCompany) {
      toast.error('Please select a company first');
      return;
    }
    
    setOcrCreating(true);
    try {
      const response = await createClientPOFromExtraction(ocrExtraction.extraction_id, selectedCompany);
      toast.success(`Client PO ${response.data.po_number} created!`);
      setOcrReviewOpen(false);
      setOcrExtraction(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create PO');
    } finally {
      setOcrCreating(false);
    }
  };

  const filteredPOs = purchaseOrders.filter(po => {
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      return (
        po.po_number?.toLowerCase().includes(search) ||
        po.client_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getFilteredClients = () => {
    if (!poForm.company_id) return [];
    return clients.filter(c => c.company_id === poForm.company_id);
  };

  return (
    <div className="space-y-6" data-testid="client-po-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Client Purchase Orders</h1>
          <p className="text-muted-foreground">Mandatory starting point for all business operations</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setOcrDialogOpen(true)} data-testid="ocr-extract-btn">
            <Sparkles className="h-4 w-4 mr-2" />
            Extract from PO
          </Button>
          <Button variant="outline" onClick={() => setCreateClientOpen(true)} data-testid="create-client-btn">
            <Users className="h-4 w-4 mr-2" />
            Add Client
          </Button>
          <Button onClick={() => setCreatePOOpen(true)} data-testid="create-po-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Client PO
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-muted-foreground">Total POs</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.by_status?.draft || 0}</div>
              <p className="text-xs text-muted-foreground">Draft</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.active || 0}</div>
              <p className="text-xs text-muted-foreground">Active</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{stats.document_status?.pending || 0}</div>
              <p className="text-xs text-muted-foreground">Pending Upload</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{stats.total_value?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground">Total Value (SAR)</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.total_remaining?.toLocaleString() || 0}</div>
              <p className="text-xs text-muted-foreground">Remaining (SAR)</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search PO# or client..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-input"
              />
            </div>
            <Select value={selectedCompany || "all"} onValueChange={(v) => setSelectedCompany(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="company-filter">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="manpower">Manpower</SelectItem>
                <SelectItem value="material">Material</SelectItem>
                <SelectItem value="transportation">Transportation</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* PO List */}
      <Card>
        <CardHeader>
          <CardTitle>Purchase Orders ({filteredPOs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : filteredPOs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No purchase orders found. Create your first Client PO to start operations.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-3 font-medium">PO Number</th>
                    <th className="pb-3 font-medium">Client</th>
                    <th className="pb-3 font-medium">Type</th>
                    <th className="pb-3 font-medium">Company</th>
                    <th className="pb-3 font-medium">Value</th>
                    <th className="pb-3 font-medium">Document</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPOs.map(po => {
                    const TypeIcon = poTypeIcons[po.po_type] || FileText;
                    return (
                      <tr key={po.id} className="border-b hover:bg-muted/50" data-testid={`po-row-${po.id}`}>
                        <td className="py-3 font-medium">{po.po_number}</td>
                        <td className="py-3">{po.client_name}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <TypeIcon className="h-4 w-4" />
                            <span className="capitalize">{po.po_type}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {po.po_value_type === 'open' ? '(Open/Recurring)' : '(Specified Value)'}
                          </span>
                        </td>
                        <td className="py-3">{po.company_name}</td>
                        <td className="py-3">
                          {po.po_value_type === 'open' 
                            ? <span className="text-muted-foreground">Open</span>
                            : `${po.currency} ${po.total_value?.toLocaleString()}`
                          }
                        </td>
                        <td className="py-3">
                          {po.document_uploaded ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Uploaded
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-800">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </td>
                        <td className="py-3">
                          <Badge className={statusColors[po.status]}>
                            {po.status}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedPO(po);
                                setViewPOOpen(true);
                              }}
                              data-testid={`view-po-${po.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!po.document_uploaded && po.status !== 'cancelled' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setSelectedPO(po);
                                  setUploadOpen(true);
                                }}
                                data-testid={`upload-doc-${po.id}`}
                              >
                                <Upload className="h-4 w-4" />
                              </Button>
                            )}
                            {po.document_uploaded && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewDocument(po)}
                                data-testid={`download-doc-${po.id}`}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                            {po.status === 'draft' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600"
                                onClick={() => handleCancelPO(po.id)}
                                data-testid={`cancel-po-${po.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create PO Dialog */}
      <Dialog open={createPOOpen} onOpenChange={setCreatePOOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Client Purchase Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Operating Company *</Label>
                <Select 
                  value={poForm.company_id} 
                  onValueChange={(v) => setPOForm({...poForm, company_id: v, client_id: ''})}
                >
                  <SelectTrigger data-testid="po-company-select">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client *</Label>
                <Select 
                  value={poForm.client_id} 
                  onValueChange={(v) => setPOForm({...poForm, client_id: v})}
                  disabled={!poForm.company_id}
                >
                  <SelectTrigger data-testid="po-client-select">
                    <SelectValue placeholder={poForm.company_id ? "Select client" : "Select company first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilteredClients().map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>PO Number *</Label>
                <Input
                  value={poForm.po_number}
                  onChange={(e) => setPOForm({...poForm, po_number: e.target.value})}
                  placeholder="e.g., CPO-2026-001"
                  data-testid="po-number-input"
                />
              </div>
              <div>
                <Label>PO Type *</Label>
                <Select 
                  value={poForm.po_type} 
                  onValueChange={(v) => setPOForm({...poForm, po_type: v})}
                >
                  <SelectTrigger data-testid="po-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equipment">Equipment Rental</SelectItem>
                    <SelectItem value="manpower">Manpower Supply</SelectItem>
                    <SelectItem value="material">Material Supply</SelectItem>
                    <SelectItem value="transportation">Transportation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* NEW: PO Value Type - Specified or Open */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>PO Value Type *</Label>
                <Select 
                  value={poForm.po_value_type} 
                  onValueChange={(v) => setPOForm({...poForm, po_value_type: v})}
                >
                  <SelectTrigger data-testid="po-value-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="specified">Specified Value (Fixed Budget)</SelectItem>
                    <SelectItem value="open">Open / Recurring (No Fixed Value)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {poForm.po_value_type === 'specified' 
                    ? 'Consumption will be tracked against total value' 
                    : 'No consumption tracking - unlimited deliveries'}
                </p>
              </div>
              <div>
                <Label>Equipment Category</Label>
                <Input
                  value={poForm.equipment_category}
                  onChange={(e) => setPOForm({...poForm, equipment_category: e.target.value})}
                  placeholder="e.g., Heavy Machinery, Vehicles..."
                />
              </div>
            </div>

            {/* NEW: Rental Details (for Equipment PO) */}
            {poForm.po_type === 'equipment' && (
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <h4 className="font-semibold mb-3 text-blue-800">Rental Details</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label>Rental Basis *</Label>
                    <Select 
                      value={poForm.rental_basis} 
                      onValueChange={(v) => setPOForm({...poForm, rental_basis: v})}
                    >
                      <SelectTrigger data-testid="rental-basis-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily Rate</SelectItem>
                        <SelectItem value="weekly">Weekly Rate</SelectItem>
                        <SelectItem value="monthly">Monthly Rate</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rental Rate ({poForm.currency})</Label>
                    <Input
                      type="number"
                      value={poForm.rental_rate}
                      onChange={(e) => setPOForm({...poForm, rental_rate: parseFloat(e.target.value) || 0})}
                      placeholder={`Rate per ${poForm.rental_basis || 'period'}`}
                      data-testid="rental-rate-input"
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label>PO Date</Label>
                <Input
                  type="date"
                  value={poForm.po_date}
                  onChange={(e) => setPOForm({...poForm, po_date: e.target.value})}
                  data-testid="po-date-input"
                />
              </div>
              <div>
                <Label>Valid From</Label>
                <Input
                  type="date"
                  value={poForm.valid_from}
                  onChange={(e) => setPOForm({...poForm, valid_from: e.target.value})}
                />
              </div>
              <div>
                <Label>Valid To (Expiry) *</Label>
                <Input
                  type="date"
                  value={poForm.valid_to}
                  onChange={(e) => setPOForm({...poForm, valid_to: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label>Currency</Label>
                <Select 
                  value={poForm.currency} 
                  onValueChange={(v) => setPOForm({...poForm, currency: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SAR">SAR</SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Total Value {poForm.po_value_type === 'specified' && '*'}</Label>
                <Input
                  type="number"
                  value={poForm.total_value}
                  onChange={(e) => setPOForm({...poForm, total_value: parseFloat(e.target.value) || 0})}
                  data-testid="po-value-input"
                  disabled={poForm.po_value_type === 'open'}
                />
                {poForm.po_value_type === 'open' && (
                  <p className="text-xs text-muted-foreground mt-1">N/A for Open PO</p>
                )}
              </div>
              <div>
                <Label>VAT Rate (%)</Label>
                <Input
                  type="number"
                  value={poForm.vat_rate}
                  onChange={(e) => setPOForm({...poForm, vat_rate: parseFloat(e.target.value) || 15})}
                />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input
                value={poForm.notes}
                onChange={(e) => setPOForm({...poForm, notes: e.target.value})}
                placeholder="Additional notes..."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCreatePOOpen(false)}>Cancel</Button>
              <Button onClick={handleCreatePO} data-testid="submit-po-btn">Create PO</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Client Dialog */}
      <Dialog open={createClientOpen} onOpenChange={setCreateClientOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Operating Company *</Label>
                <Select 
                  value={clientForm.company_id} 
                  onValueChange={(v) => setClientForm({...clientForm, company_id: v})}
                >
                  <SelectTrigger data-testid="client-company-select">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Client Name *</Label>
                <Input
                  value={clientForm.name}
                  onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                  placeholder="Client legal name"
                  data-testid="client-name-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Trade Name</Label>
                <Input
                  value={clientForm.trade_name}
                  onChange={(e) => setClientForm({...clientForm, trade_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Registration Number</Label>
                <Input
                  value={clientForm.registration_number}
                  onChange={(e) => setClientForm({...clientForm, registration_number: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>VAT Number</Label>
                <Input
                  value={clientForm.vat_number}
                  onChange={(e) => setClientForm({...clientForm, vat_number: e.target.value})}
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  value={clientForm.country}
                  onChange={(e) => setClientForm({...clientForm, country: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Address</Label>
              <Input
                value={clientForm.address}
                onChange={(e) => setClientForm({...clientForm, address: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <Label>Contact Name</Label>
                <Input
                  value={clientForm.contact_name}
                  onChange={(e) => setClientForm({...clientForm, contact_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  type="email"
                  value={clientForm.contact_email}
                  onChange={(e) => setClientForm({...clientForm, contact_email: e.target.value})}
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={clientForm.contact_phone}
                  onChange={(e) => setClientForm({...clientForm, contact_phone: e.target.value})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>Payment Terms (days)</Label>
                <Input
                  type="number"
                  value={clientForm.payment_terms}
                  onChange={(e) => setClientForm({...clientForm, payment_terms: parseInt(e.target.value) || 30})}
                />
              </div>
              <div>
                <Label>Credit Limit</Label>
                <Input
                  type="number"
                  value={clientForm.credit_limit}
                  onChange={(e) => setClientForm({...clientForm, credit_limit: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setCreateClientOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateClient} data-testid="submit-client-btn">Add Client</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog open={viewPOOpen} onOpenChange={setViewPOOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">PO Number</Label>
                  <p className="font-medium">{selectedPO.po_number}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className={statusColors[selectedPO.status]}>
                    {selectedPO.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Client</Label>
                  <p className="font-medium">{selectedPO.client_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Operating Company</Label>
                  <p className="font-medium">{selectedPO.company_name}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="font-medium capitalize">{selectedPO.po_type}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Value Type</Label>
                  <p className="font-medium capitalize">{selectedPO.po_value_type === 'specified' ? 'Specified Value' : 'Open / Recurring'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">PO Date</Label>
                  <p className="font-medium">{selectedPO.po_date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Valid Until</Label>
                  <p className="font-medium">{selectedPO.valid_to || 'N/A'}</p>
                </div>
              </div>

              {/* Rental Details for Equipment POs */}
              {selectedPO.po_type === 'equipment' && selectedPO.rental_basis && (
                <div className="border rounded-lg p-3 bg-blue-50/50">
                  <h4 className="font-semibold mb-2 text-blue-800">Rental Details</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Rental Basis</Label>
                      <p className="font-medium capitalize">{selectedPO.rental_basis}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Rate</Label>
                      <p className="font-medium">{selectedPO.currency} {selectedPO.rental_rate?.toLocaleString()} / {selectedPO.rental_basis}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-muted-foreground">Total Value</Label>
                  <p className="font-medium">{selectedPO.currency} {selectedPO.total_value?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Invoiced</Label>
                  <p className="font-medium">{selectedPO.currency} {selectedPO.invoiced_amount?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Remaining</Label>
                  <p className="font-medium">{selectedPO.currency} {selectedPO.remaining_amount?.toLocaleString()}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Document Status</Label>
                  {selectedPO.document_uploaded ? (
                    <Badge className="bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Uploaded: {selectedPO.document_filename}
                    </Badge>
                  ) : (
                    <Badge className="bg-orange-100 text-orange-800">
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Pending Upload
                    </Badge>
                  )}
                </div>
                <div>
                  <Label className="text-muted-foreground">Delivery Notes</Label>
                  <p className="font-medium">{selectedPO.delivery_notes_count || 0}</p>
                </div>
              </div>

              {selectedPO.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p>{selectedPO.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload PO Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Upload the client&apos;s Purchase Order document (PDF or image).
              This is <span className="font-semibold text-red-600">MANDATORY</span> before creating delivery notes.
            </p>
            <Input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  handleUploadDocument(e.target.files[0]);
                }
              }}
              data-testid="upload-file-input"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* OCR Extract Dialog */}
      <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Extract Data from Client PO
            </DialogTitle>
            <DialogDescription>
              Upload a Client PO document (image or PDF) and our AI will extract the data automatically.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <input
                ref={ocrFileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleOcrFileSelect}
                className="hidden"
                data-testid="ocr-file-input"
              />
              {ocrExtracting ? (
                <div className="space-y-3">
                  <Loader2 className="h-12 w-12 mx-auto text-purple-600 animate-spin" />
                  <p className="text-muted-foreground">Extracting PO data with AI...</p>
                  <p className="text-xs text-muted-foreground">This may take 10-20 seconds</p>
                </div>
              ) : (
                <>
                  <FileText className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Upload a Client PO document<br />
                    <span className="text-sm">JPEG, PNG, WebP, or PDF (max 20MB)</span>
                  </p>
                  <Button onClick={() => ocrFileInputRef.current?.click()} data-testid="ocr-upload-btn">
                    <Upload className="h-4 w-4 mr-2" />
                    Select File
                  </Button>
                </>
              )}
            </div>
            <div className="text-sm text-muted-foreground bg-slate-50 rounded-lg p-3">
              <p className="font-medium mb-1">AI will extract:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>PO Number and Date</li>
                <li>Client/Vendor Name</li>
                <li>Line Items (description, quantity, unit, rate)</li>
                <li>Total Amount and Currency</li>
                <li>Validity Period</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* OCR Review Dialog */}
      <Dialog open={ocrReviewOpen} onOpenChange={setOcrReviewOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Review Extracted Data
            </DialogTitle>
            <DialogDescription>
              Review the data extracted from your PO document. You can edit fields before creating the PO.
            </DialogDescription>
          </DialogHeader>
          
          {ocrExtraction?.extracted_data && (
            <div className="space-y-6">
              {/* Confidence indicator */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Confidence:</span>
                <Badge className={
                  ocrExtraction.confidence >= 0.8 ? 'bg-green-100 text-green-800' :
                  ocrExtraction.confidence >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }>
                  {Math.round((ocrExtraction.confidence || 0) * 100)}%
                </Badge>
              </div>

              {/* Basic Info */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">PO Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">PO Number</Label>
                      <Input 
                        value={ocrExtraction.extracted_data.po_number || ''} 
                        onChange={(e) => setOcrExtraction({
                          ...ocrExtraction,
                          extracted_data: {...ocrExtraction.extracted_data, po_number: e.target.value}
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">PO Date</Label>
                      <Input 
                        type="date"
                        value={ocrExtraction.extracted_data.po_date || ''} 
                        onChange={(e) => setOcrExtraction({
                          ...ocrExtraction,
                          extracted_data: {...ocrExtraction.extracted_data, po_date: e.target.value}
                        })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Client/Vendor Name</Label>
                    <Input 
                      value={ocrExtraction.extracted_data.client_name || ocrExtraction.extracted_data.vendor_name || ''} 
                      onChange={(e) => setOcrExtraction({
                        ...ocrExtraction,
                        extracted_data: {...ocrExtraction.extracted_data, client_name: e.target.value}
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Currency</Label>
                      <Input 
                        value={ocrExtraction.extracted_data.currency || 'SAR'} 
                        onChange={(e) => setOcrExtraction({
                          ...ocrExtraction,
                          extracted_data: {...ocrExtraction.extracted_data, currency: e.target.value}
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Total Value</Label>
                      <Input 
                        type="number"
                        value={ocrExtraction.extracted_data.total_value || 0} 
                        onChange={(e) => setOcrExtraction({
                          ...ocrExtraction,
                          extracted_data: {...ocrExtraction.extracted_data, total_value: parseFloat(e.target.value)}
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">PO Type</Label>
                      <Select 
                        value={ocrExtraction.extracted_data.po_type || 'equipment'} 
                        onValueChange={(v) => setOcrExtraction({
                          ...ocrExtraction,
                          extracted_data: {...ocrExtraction.extracted_data, po_type: v}
                        })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equipment">Equipment</SelectItem>
                          <SelectItem value="manpower">Manpower</SelectItem>
                          <SelectItem value="material">Material</SelectItem>
                          <SelectItem value="transportation">Transportation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Valid From</Label>
                      <Input 
                        type="date"
                        value={ocrExtraction.extracted_data.valid_from || ''} 
                        onChange={(e) => setOcrExtraction({
                          ...ocrExtraction,
                          extracted_data: {...ocrExtraction.extracted_data, valid_from: e.target.value}
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Valid To</Label>
                      <Input 
                        type="date"
                        value={ocrExtraction.extracted_data.valid_to || ''} 
                        onChange={(e) => setOcrExtraction({
                          ...ocrExtraction,
                          extracted_data: {...ocrExtraction.extracted_data, valid_to: e.target.value}
                        })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Line Items */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-sm">Line Items ({ocrExtraction.extracted_data.items?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {ocrExtraction.extracted_data.items?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Description</TableHead>
                          <TableHead className="w-20">Qty</TableHead>
                          <TableHead className="w-20">Unit</TableHead>
                          <TableHead className="w-28">Rate</TableHead>
                          <TableHead className="w-28">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ocrExtraction.extracted_data.items.map((item, idx) => (
                          <TableRow key={idx}>
                            <TableCell>
                              <Input 
                                value={item.description || ''} 
                                onChange={(e) => {
                                  const newItems = [...ocrExtraction.extracted_data.items];
                                  newItems[idx] = {...newItems[idx], description: e.target.value};
                                  setOcrExtraction({
                                    ...ocrExtraction,
                                    extracted_data: {...ocrExtraction.extracted_data, items: newItems}
                                  });
                                }}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number"
                                value={item.quantity || 0} 
                                onChange={(e) => {
                                  const newItems = [...ocrExtraction.extracted_data.items];
                                  newItems[idx] = {...newItems[idx], quantity: parseFloat(e.target.value)};
                                  setOcrExtraction({
                                    ...ocrExtraction,
                                    extracted_data: {...ocrExtraction.extracted_data, items: newItems}
                                  });
                                }}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                value={item.unit || ''} 
                                onChange={(e) => {
                                  const newItems = [...ocrExtraction.extracted_data.items];
                                  newItems[idx] = {...newItems[idx], unit: e.target.value};
                                  setOcrExtraction({
                                    ...ocrExtraction,
                                    extracted_data: {...ocrExtraction.extracted_data, items: newItems}
                                  });
                                }}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell>
                              <Input 
                                type="number"
                                value={item.unit_price || item.rate || 0} 
                                onChange={(e) => {
                                  const newItems = [...ocrExtraction.extracted_data.items];
                                  newItems[idx] = {...newItems[idx], unit_price: parseFloat(e.target.value)};
                                  setOcrExtraction({
                                    ...ocrExtraction,
                                    extracted_data: {...ocrExtraction.extracted_data, items: newItems}
                                  });
                                }}
                                className="h-8"
                              />
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {((item.quantity || 0) * (item.unit_price || item.rate || 0)).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-center py-4 text-muted-foreground">No line items extracted</p>
                  )}
                </CardContent>
              </Card>

              {/* Company Selection */}
              <Card className="border-purple-200 bg-purple-50">
                <CardContent className="p-4">
                  <Label className="text-sm font-medium">Select Company to Create PO *</Label>
                  <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-2">
                    The client will be created automatically if it doesn't exist.
                  </p>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => { setOcrReviewOpen(false); setOcrExtraction(null); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleCreatePOFromOCR} 
                  disabled={ocrCreating || !selectedCompany}
                  data-testid="create-po-from-ocr-btn"
                >
                  {ocrCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Create Client PO
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientPOPage;
